import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Camera, ImageUp, ShieldCheck } from "lucide-react";

function decodeFromCanvas(canvas, source, w, h) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  canvas.width = w;
  canvas.height = h;
  ctx.drawImage(source, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h);
  return jsQR(data.data, w, h, { inversionAttempts: "attemptBoth" });
}

export default function QrScanner({ onResult }) {
  const video = useRef(null);
  const canvas = useRef(null);
  const stream = useRef(null);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");

  const stop = () => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  };
  useEffect(() => stop, []);

  const start = async () => {
    setErr("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Camera is not available in this browser. Upload a photo of the QR code instead.");
      return;
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      video.current.srcObject = stream.current;
      await video.current.play();
      setState("scanning");
      const tick = () => {
        if (!stream.current) return;
        const v = video.current;
        if (v.readyState === v.HAVE_ENOUGH_DATA) {
          const scale = Math.min(1, 800 / v.videoWidth);
          const code = decodeFromCanvas(canvas.current, v, Math.round(v.videoWidth * scale), Math.round(v.videoHeight * scale));
          if (code?.data) { stop(); setState("idle"); onResult(code.data); return; }
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } catch (e) {
      setState("idle");
      setErr(e.name === "NotAllowedError" ? "Camera permission was denied. Allow camera access in your browser settings, or upload a photo of the QR code below." : "Could not start the camera. Upload a photo of the QR code instead.");
    }
  };

  const fromFile = async (file) => {
    if (!file) return;
    setErr("");
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      const code = decodeFromCanvas(canvas.current, img, Math.round(img.width * scale), Math.round(img.height * scale));
      URL.revokeObjectURL(url);
      if (code?.data) onResult(code.data);
      else setErr("No QR code found in this image. Try a sharper, closer photo.");
    };
    img.src = url;
  };

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-2xl bg-slate-900" style={{ aspectRatio: "4/3" }}>
        <video ref={video} playsInline muted className={`h-full w-full object-cover ${state === "scanning" ? "" : "hidden"}`} data-testid="qr-video" />
        {state === "scanning" && <div className="pointer-events-none absolute inset-[18%] rounded-2xl border-4 border-teal-400/80" />}
        {state !== "scanning" && (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-slate-300">
            <ShieldCheck className="h-8 w-8 text-teal-400" />
            <p className="max-w-xs text-sm">Your browser will ask for camera permission. The video stays on your device — only the decoded QR text is sent to your workspace. Links are never opened automatically.</p>
          </div>
        )}
      </div>
      <canvas ref={canvas} className="hidden" />
      {err && <p data-testid="qr-error" className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800">{err}</p>}
      <div className="grid grid-cols-2 gap-3">
        {state === "scanning" ? (
          <button data-testid="qr-stop-btn" onClick={() => { stop(); setState("idle"); }} className="cd-btn-secondary">Stop camera</button>
        ) : (
          <button data-testid="qr-start-camera-btn" onClick={start} className="cd-btn-primary"><Camera className="h-5 w-5" />Start camera</button>
        )}
        <label className="cd-btn-secondary cursor-pointer">
          <ImageUp className="h-5 w-5" />Upload QR image
          <input data-testid="qr-upload-input" type="file" accept="image/*" className="hidden" onChange={(e) => fromFile(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
}
